/**
 * lib/clientDiagnostics.js — télescope léger pour bug récurrent
 * "menu vide / bon de travail incomplet".
 *
 * - reportClientEvent(eventType, severity, context)
 *   → POST /api/diagnostics/client-event en fire-and-forget.
 *   → Jamais bloquant. Jamais throw. Silencieux si endpoint down.
 *   → Dédup en mémoire : un même event_type + signature de contexte n'est
 *     pas réenvoyé plus d'une fois toutes les 60 secondes.
 *   → Ne JAMAIS envoyer de token / mot de passe / Authorization header.
 */

const API_URL =
  (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) ||
  '';

const VALID_TYPES = new Set([
  'permissions_fetch_failed',
  'user_became_null',
  'api_error',
  'sidebar_empty_rendered',
  'auth_loading_stuck',
  // 🔎 [BLOC 2 — 2026-02-26] Instrumentation Pause PM (log-only, log-only, aucun fix).
  //   Objectif : observer côté prod POURQUOI la modale « 15 min pause » ne s'affiche
  //   pas pour certains employés. Voir RCA_V13_62_BLOC2_PAUSE_PM_INVESTIGATION.md.
  'pause_pm_check_result',   // résultat de fetchPausePmApplicableWithRetry (applicable, reason, totaux)
  'pause_pm_cache_lookup',   // hit du cache local `pausePmLocalCache` (raison shortcut sans modale)
  'pause_pm_retry_failed',   // 3 tentatives échouées → toast bloquant ou fallback cache
]);
const VALID_SEVERITIES = new Set(['warning', 'error', 'critical', 'info']);

const DEDUP_TTL_MS = 60 * 1000;
const _lastSentAt = new Map();

function _signature(eventType, context) {
  // Clé stable, indépendante des valeurs numériques volatiles
  try {
    const keys = context ? Object.keys(context).sort().join('|') : '';
    const status = context && (context.status || context.code || context.reason || '');
    const url = context && (context.url || context.context || '');
    return `${eventType}::${keys}::${status}::${url}`;
  } catch {
    return eventType;
  }
}

function _safeUserSnapshot() {
  try {
    // Lit le token uniquement pour décoder user_id/email/role — ne RENVOIE
    // PAS le token. Reste robuste si JWT est cassé / absent.
    const t = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!t || typeof t !== 'string') return {};
    const parts = t.split('.');
    if (parts.length !== 3) return {};
    const payload = JSON.parse(atob(parts[1]));
    return {
      user_id: payload?.user_id || '',
      user_email: payload?.email || '',
      user_role: payload?.role || '',
    };
  } catch {
    return {};
  }
}

export function reportClientEvent(eventType, severity, context) {
  try {
    if (!VALID_TYPES.has(eventType)) return;
    const sev = VALID_SEVERITIES.has(severity) ? severity : 'warning';

    const sig = _signature(eventType, context);
    const now = Date.now();
    const last = _lastSentAt.get(sig) || 0;
    if (now - last < DEDUP_TTL_MS) return;
    _lastSentAt.set(sig, now);
    // Limite la taille du dedup map
    if (_lastSentAt.size > 500) {
      // Supprime les entrées expirées
      for (const [k, v] of _lastSentAt) {
        if (now - v > DEDUP_TTL_MS) _lastSentAt.delete(k);
      }
    }

    const snap = _safeUserSnapshot();
    const body = {
      event_type: eventType,
      severity: sev,
      user_id: snap.user_id || '',
      user_email: snap.user_email || '',
      user_role: snap.user_role || '',
      url: typeof window !== 'undefined' ? window.location.href.slice(0, 500) : '',
      user_agent: typeof navigator !== 'undefined' ? (navigator.userAgent || '').slice(0, 500) : '',
      context: context || {},
      client_timestamp: new Date().toISOString(),
    };

    // Fire-and-forget : on n'attend pas la réponse, on n'inject pas d'Authorization
    // header (auth optionnelle côté backend). On utilise fetch native pour
    // contourner les intercepteurs axios qui pourraient retry/boucler.
    const url = `${API_URL}/api/diagnostics/client-event`;
    try {
      // sendBeacon est idéal pour fire-and-forget (même en pageunload), mais
      // certaines implémentations ne supportent que les requêtes < 64 KB.
      const payload = JSON.stringify(body);
      if (navigator?.sendBeacon && payload.length < 60_000) {
        const blob = new Blob([payload], { type: 'application/json' });
        const sent = navigator.sendBeacon(url, blob);
        if (sent) return;
      }
      // Fallback : fetch keep-alive
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {});
    } catch {
      /* silencieux */
    }
  } catch {
    /* jamais throw */
  }
}

export default reportClientEvent;
